/* eslint-disable react-hooks/rules-of-hooks */
import { keyframes } from '@emotion/react'
import classNames from 'classnames'
import type { FC } from 'react'
import React from 'react'

import type { LoadingSpinnerProps } from './types'
import useResponsiveValue from '../../../../hooks/use-responsive-value'
import type { Theme } from '../../../../theme-provider/types'
import { useSafeThemeUI } from '../../../../theme-provider/use-theme-ui'
import { get } from '../../../../utils/object'
import { resolveSize } from '../../../../utils/size-utils'
import { BoxPrivate } from '../../box/box'
import Svg, { Circle } from '../../svg'

const animLinearRotate = keyframes({
  '0%': { transform: 'rotate(0deg)' },
  '100%': { transform: 'rotate(360deg)' }
})
const pi = 3.14159265
const strokeDashoffset05 = (1 - 0.05) * pi * 2 * 45 + `px`
const strokeDashoffset8 = (1 - 0.8) * pi * 2 * 45 + `px`
const animStrokeRotate = keyframes({
  '0%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotate(0)'
  },
  '12.5%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotate(0)'
  },
  '12.5001%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotateX(180deg) rotate(72.5deg)'
  },
  '25%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotateX(180deg) rotate(72.5deg)'
  },
  '25.0001%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotate(270deg)'
  },
  '37.5%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotate(270deg)'
  },
  '37.5001%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotateX(180deg) rotate(161.5deg)'
  },
  '50%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotateX(180deg) rotate(161.5deg)'
  },
  '50.0001%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotate(180deg)'
  },
  '62.5%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotate(180deg)'
  },
  '62.5001%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotateX(180deg) rotate(251.5deg)'
  },
  '75%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotateX(180deg) rotate(251.5deg)'
  },
  '75.0001%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotate(90deg)'
  },
  '87.5%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotate(90deg)'
  },
  '87.5001%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotateX(180deg) rotate(341.5deg)'
  },
  '100%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotateX(180deg) rotate(341.5deg)'
  }
})

const LoadingSpinner: FC<LoadingSpinnerProps> = React.forwardRef<
  HTMLDivElement,
  LoadingSpinnerProps & { theme?: Theme }
>(function LoadingSpinner(props, ref) {
  const theme = useSafeThemeUI(props)
  const {
    className,
    size = 'md',
    strokeWidth = 4, //placeholder to render color, strokewidth is set on circle
    title = 'Loading...',
    color = 'primary',
    animationDuration = 2000,
    ...rest
  } = props

  const circleAnimationDuration = animationDuration * 2 + 'ms'
  const spinnerSize = resolveSize(size, get(theme, 'sizes.spinner'))
  const _size = Array.isArray(spinnerSize)
    ? useResponsiveValue(spinnerSize)
    : spinnerSize
  return (
    <BoxPrivate
      color={color}
      className={classNames(
        {
          'csg-tui-progress-spinner': true
        },
        className
      )}
      ref={ref}
      width={_size}
      height={_size}
      role="progressbar"
      __css={{
        position: 'relative',
        overflow: 'hidden'
      }}
      {...rest}
    >
      <Svg
        id="loading_spinner"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        strokeWidth={strokeWidth}
        role="img"
        __css={{
          transform: 'rotate(-90deg)',
          transformOrigin: 'center',
          overflow: 'visible',
          animationName: animLinearRotate.toString(),
          animationDuration: animationDuration + 'ms', // 2000
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
          padding: '1px'
        }}
      >
        <title>{title}</title>
        <Circle
          cx="50%"
          cy="50%"
          r="45"
          __css={{
            fill: 'transparent',
            transformOrigin: 'center',
            transition: 'stroke-dashoffset 225ms linear',
            transitionProperty: 'stroke',
            animationName: animStrokeRotate.toString(),
            animationDuration: circleAnimationDuration, // 4000
            animationTimingFunction: 'cubic-bezier(0.35, 0, 0.25, 1)',
            animationIterationCount: 'infinite',
            strokeDasharray: '282.743px',
            strokeWidth: '10%'
          }}
        />
      </Svg>
    </BoxPrivate>
  )
})

export default LoadingSpinner
